import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DecodedToken } from '../types/auth.types'

type AuthStore = {
  idToken: string | null
  refreshToken: string | null
  accessToken: string | null
  userId: string | null
  email: string | null
  lastLoginAt: string | null
  setAuthTokens: (tokens: {
    idToken: string
    refreshToken: string
    accessToken: string
  }) => void
  clearAuth: () => void
  refreshTokens: () => Promise<void>
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      idToken: null,
      refreshToken: null,
      accessToken: null,
      userId: null,
      email: null,
      lastLoginAt: null,

      setAuthTokens: ({ idToken, refreshToken, accessToken }) => {
        let decoded: DecodedToken | null = null
        try {
          decoded = jwtDecode<DecodedToken>(idToken)
        } catch {
          console.error('Failed to decode token:', idToken)
        }

        set({
          idToken,
          refreshToken,
          accessToken,
          userId: decoded?.userId ?? null,
          email: decoded?.email ?? null,
          lastLoginAt: decoded?.lastLoginAt ?? null
        })
      },
      clearAuth: () =>
        set({
          idToken: null,
          refreshToken: null,
          accessToken: null,
          userId: null,
          email: null,
          lastLoginAt: null
        }),
      refreshTokens: async () => {
        const { refreshToken, setAuthTokens } = get()

        if (!refreshToken) {
          get().clearAuth()
          return
        }

        try {
          // Use axios instead of axiosClient to avoid the interceptor and prevent a loop
          const baseUrl = import.meta.env.VITE_API_BASE_URL
          const response = await axios.post(`${baseUrl}/auth/refresh-tokens`, {
            refreshToken
          })

          const {
            idToken,
            accessToken,
            refreshToken: newRefreshToken
          } = {
            idToken: response.data.data.idToken,
            accessToken: response.data.data.accessToken,
            refreshToken: response.data.data.refreshToken
          }

          setAuthTokens({
            idToken,
            accessToken,
            refreshToken: newRefreshToken
          })
        } catch {
          get().clearAuth()
        }
      }
    }),
    {
      name: 'auth-store'
    }
  )
)
